"use client";

import React, { useEffect, useRef, useState } from 'react';
import { useSession } from 'next-auth/react';
import { toAbsoluteMediaUrl } from '@/lib/media';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';
import { track, postAnalytics } from '@/lib/analytics';
import { scrubContactInfo } from '@/lib/scrubContact';

// In-memory view POST de-duplication cache (5 minutes per video)
const VIEW_DEDUP_MS = 5 * 60 * 1000;
const viewPostCache: Map<string, number> = new Map();

export default function VideoPlayerClient({
  id,
  streamUrl,
  viewUrl,
  posterUrl,
  unsafe = false,
  title,
  description,
  viewCount,
  gated = false,
  likeCount = 0,
  isLiked = false,
  commentCount,
  createdAt,
  uploader,
  initialDurationSec,
  showUnderPlayerMeta = true,
  titleHeading = 'h1',
  onStreamResolved,
  onMeta,
}: {
  id: number | string;
  streamUrl: string;
  viewUrl: string;
  posterUrl?: string | null;
  unsafe?: boolean;
  title: string;
  description?: string;
  viewCount?: number | null;
  gated?: boolean;
  likeCount?: number;
  isLiked?: boolean;
  commentCount?: number | null;
  createdAt?: string | null;
  uploader?: { displayName?: string; profileUrl?: string; avatarUrl?: string } | null;
  initialDurationSec?: number | null;
  showUnderPlayerMeta?: boolean;
  // Heading level for the under-player title. Defaults to 'h1' because on the
  // recovery shell (videos/[id]/page.tsx ClientShell) this is the page's ONLY
  // heading, so it must stay the h1 there. The full detail render passes 'h2'
  // instead: it already prints the same title in its own hero h1, and shipping
  // two h1s with identical text on every video page is an SEO defect.
  titleHeading?: 'h1' | 'h2';
  onStreamResolved?: (url: string) => void;
  onMeta?: (meta: { durationSec?: number; width?: number; height?: number }) => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<any | null>(null);
  const ttfpStartRef = useRef<number | null>(null);
  const [viewed, setViewed] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [retrySeq, setRetrySeq] = useState(0);
  const [views, setViews] = useState<number | null>(typeof viewCount === 'number' ? viewCount : null);
  const { data: session, status } = useSession();
  const effectiveGated = gated && status !== 'authenticated';
  const isAuthed = status === 'authenticated';
  // Poster fallback is the opaque 1280x720 JPEG, not the RGBA logo. Same defect
  // Search Console flagged as "Thumbnail is transparent" on the metadata path.
  const initialPoster = posterUrl ? toAbsoluteMediaUrl(posterUrl) : '/img/video-fallback.jpg';
  const [poster, setPoster] = useState<string>(initialPoster);
  const lastTimeRef = useRef(0);
  const accSecRef = useRef(0);
  const playingRef = useRef(false);
  const [duration, setDuration] = useState<number>(Math.max(0, Number(initialDurationSec || 0)));
  const debugMediaPerf = process.env.NEXT_PUBLIC_DEBUG_MEDIA_PERF === '1';
  const MAX_INITIAL_BITRATE = Number(process.env.NEXT_PUBLIC_MAX_INITIAL_BITRATE || '1200000') || 1200000;
  // Offline detection and analytics spam guard
  const [offline, setOffline] = useState<boolean>(false);
  const offlineSentRef = useRef<boolean>(false);
  const onlineSentRef = useRef<boolean>(false);
  // Overlay focus management & trap
  const spinRef = useRef<HTMLDivElement | null>(null);
  const offRef = useRef<HTMLDivElement | null>(null);
  const errRef = useRef<HTMLDivElement | null>(null);
  const gateRef = useRef<HTMLDivElement | null>(null);

  // Phase D §D.5 — Picture-in-Picture. Feature-detect via document.pictureInPictureEnabled
  // and hide the button on unsupported browsers (Firefox before 71, mobile
  // Safari iPad-only, etc.). Toggling on the active video uses the standard
  // requestPictureInPicture / exitPictureInPicture pair.
  const [pipSupported, setPipSupported] = useState<boolean>(false);
  const [pipActive, setPipActive] = useState<boolean>(false);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    setPipSupported(!!(document as any).pictureInPictureEnabled);
  }, []);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onEnter = () => setPipActive(true);
    const onLeave = () => setPipActive(false);
    document.addEventListener('enterpictureinpicture', onEnter);
    document.addEventListener('leavepictureinpicture', onLeave);
    return () => {
      document.removeEventListener('enterpictureinpicture', onEnter);
      document.removeEventListener('leavepictureinpicture', onLeave);
    };
  }, []);
  const togglePiP = async () => {
    const el = videoRef.current;
    if (!el) return;
    try {
      if ((document as any).pictureInPictureElement) {
        await (document as any).exitPictureInPicture();
      } else if ((document as any).pictureInPictureEnabled) {
        await (el as any).requestPictureInPicture();
      }
    } catch {
      // PiP can fail if the user denies, the video isn't ready, or DRM
      // protected content (n/a here). Non-fatal — just don't toggle.
    }
  };

  // Phase D §D.3 — issue CloudFront signed cookies for this video before
  // the player asks the api for the stream URL. The cookies cover the HLS
  // master + every segment under transcoded/{id}/ for 4 hours. Direct
  // browser→api call (not via the Next.js proxy) because the proxy's
  // header-copy collapses multi-Set-Cookie into one comma-joined string
  // that the browser refuses to parse.
  //
  // INERT today: CloudFront distribution does not yet require signed
  // cookies on /transcoded/* (no key group attached). The api may also
  // respond with `signed=false` if no CloudFront key pair is configured.
  // Either way, playback works — this just primes the cookie jar for
  // when Phase D §D.3's CDN-side change lands.
  useEffect(() => {
    if (status !== 'authenticated') return;
    if (typeof window === 'undefined') return;
    const token =
      (session as any)?.user?.accessToken || (session as any)?.accessToken;
    if (!token) return;
    const base = (
      process.env.NEXT_PUBLIC_API_BASE_URL ||
      process.env.NEXT_PUBLIC_API_BASE_HTTP ||
      ''
    ).replace(/\/+$/, '');
    if (!base) return; // Same-origin would route via the proxy; skip.
    let cancelled = false;
    (async () => {
      try {
        await fetch(`${base}/api/video/v2/${id}/playback-token`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
      } catch {
        // Non-fatal — playback still works without cookies until CDN
        // enforces signing.
      }
    })();
    return () => { cancelled = true; };
  }, [status, session, id]);

  function onTrapKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Tab') return;
    const root = e.currentTarget as HTMLElement;
    const nodes = Array.from(root.querySelectorAll<HTMLElement>(
      'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
    )).filter(el => !el.hasAttribute('disabled') && el.tabIndex >= 0);
    if (nodes.length === 0) {
      e.preventDefault(); root.focus(); return;
    }
    const first = nodes[0]; const last = nodes[nodes.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey) {
      if (!active || active === first || active === root) { e.preventDefault(); last.focus(); }
    } else {
      if (active === last) { e.preventDefault(); first.focus(); }
    }
  }

  useEffect(() => {
    // mark start for TTFP measurement (approx. mount -> first frame)
    ttfpStartRef.current = typeof performance !== 'undefined' ? performance.now() : null;
  }, []);

  // Listen for online/offline changes
  useEffect(() => {
    // Initialize from navigator.onLine when available
    try { setOffline(typeof navigator !== 'undefined' ? (navigator.onLine === false) : false); } catch {}
    const onOff = () => {
      setOffline(true);
      setLoading(false);
      // Send at most one offline event per session/per video
      if (!offlineSentRef.current) {
        offlineSentRef.current = true;
        try { postAnalytics('video_offline', { id, ua: typeof navigator !== 'undefined' ? navigator.userAgent : '' }); } catch {}
      }
      try { setTimeout(() => offRef.current?.focus(), 0); } catch {}
    };
    const onOn = () => {
      setOffline(false);
      setErr(null);
      setLoading(true);
      // Attempt automatic resume
      setRetrySeq(s => s + 1);
      // Send one online resume paired to the offline
      if (offlineSentRef.current && !onlineSentRef.current) {
        onlineSentRef.current = true;
        try { postAnalytics('video_online_resume', { id }); } catch {}
      }
    };
    try {
      window.addEventListener('offline', onOff);
      window.addEventListener('online', onOn);
    } catch {}
    return () => {
      try {
        window.removeEventListener('offline', onOff);
        window.removeEventListener('online', onOn);
      } catch {}
    };
  }, [id]);

  // Focus overlays when visible
  useEffect(() => { if (loading && !effectiveGated && !offline) { try { setTimeout(() => spinRef.current?.focus(), 0); } catch {} } }, [loading, effectiveGated, offline]);
  useEffect(() => { if (err && !effectiveGated && !offline) { try { setTimeout(() => errRef.current?.focus(), 0); } catch {} } }, [err, effectiveGated, offline]);
  useEffect(() => { if (effectiveGated) { try { setTimeout(() => gateRef.current?.focus(), 0); } catch {} } }, [effectiveGated]);

  // Guardrail: pre-load poster to ensure it resolves; on failure, swap to default and log
  useEffect(() => {
    const src = initialPoster;
    if (!src) {
      setPoster('/img/video-fallback.jpg');
      return;
    }
    let cancelled = false;
    try {
      const img = new Image();
      img.onload = () => { if (!cancelled) setPoster(src); };
      img.onerror = () => {
        if (!cancelled) {
          setPoster('/img/video-fallback.jpg');
          try { postAnalytics('poster_load_failed', { id, url: src, ua: typeof navigator !== 'undefined' ? navigator.userAgent : '' }); } catch {}
        }
      };
      img.src = src;
    } catch {
      // fallback silently
      setPoster('/img/video-fallback.jpg');
    }
    return () => { cancelled = true };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPoster, id]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const onLoaded = () => {
      setLoading(false);
      if (debugMediaPerf && typeof performance !== 'undefined' && ttfpStartRef.current != null) {
        const ttfp = performance.now() - ttfpStartRef.current;
        try { console.log(`[MEDIA] TTFP_loadeddata_ms=${Math.round(ttfp)}`); } catch {}
      }
    };
    const onLoadedMeta = () => {
      try {
        const d = el.duration || 0;
        setDuration(d);
        try {
          if (onMeta) onMeta({ durationSec: d, width: (el as any).videoWidth || undefined, height: (el as any).videoHeight || undefined });
        } catch {}
      } catch {}
    };
    const onWaiting = () => setLoading(true);
    const onPlaying = () => {
      setLoading(false);
      if (debugMediaPerf && typeof performance !== 'undefined' && ttfpStartRef.current != null) {
        const ttfp = performance.now() - ttfpStartRef.current;
        try { console.log(`[MEDIA] TTFP_playing_ms=${Math.round(ttfp)}`); } catch {}
      }
      try { track('play', { id }); } catch {}
    };
    const onPlay = () => {
      playingRef.current = true;
      lastTimeRef.current = el.currentTime || 0;
    };
    const onPause = () => { playingRef.current = false; };
    const onTimeUpdate = async () => {
      if (viewed || !playingRef.current) return;
      const now = el.currentTime || 0;
      const delta = Math.max(0, now - lastTimeRef.current);
      lastTimeRef.current = now;
      accSecRef.current += delta;
      const d = duration || el.duration || 0;
      const threshold = d > 0 ? Math.min(5, 0.2 * d) : 5;
      if (accSecRef.current >= threshold) {
        setViewed(true);
        if (isAuthed && viewUrl) {
          // De-dup POST /view per video for 5 minutes in-memory
          const key = String(id);
          const last = viewPostCache.get(key) || 0;
          const nowTs = Date.now();
          if (nowTs - last < VIEW_DEDUP_MS) return;
          try {
            const res = await apiFetch(viewUrl, session, { method: 'POST', cache: 'no-store' });
            if (res.ok) {
              try {
                const data = await res.json();
                if (typeof data === 'number') setViews(data);
                else if (data && typeof data === 'object' && typeof (data as any).viewCount === 'number') setViews((data as any).viewCount);
              } catch {}
              // Mark successful view post
              viewPostCache.set(key, nowTs);
            }
          } catch (e: any) {
            setErr(e?.message || 'Failed to record view');
          }
        }
      }
    };
    const onError = () => {
      setLoading(false);
      setErr('Sorry, this video cannot be played. Please try again later.');
      try { postAnalytics('video_playback_error', { id, ua: typeof navigator !== 'undefined' ? navigator.userAgent : '' }); } catch {}
    };
    el.addEventListener('play', onPlay, { once: false });
    el.addEventListener('pause', onPause, { once: false });
    el.addEventListener('loadeddata', onLoaded, { once: true });
    el.addEventListener('loadedmetadata', onLoadedMeta, { once: true });
    el.addEventListener('canplay', onLoaded, { once: true });
    el.addEventListener('waiting', onWaiting);
    el.addEventListener('playing', onPlaying);
    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('error', onError);
    return () => {
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('loadeddata', onLoaded);
      el.removeEventListener('loadedmetadata', onLoadedMeta);
      el.removeEventListener('canplay', onLoaded);
      el.removeEventListener('waiting', onWaiting);
      el.removeEventListener('playing', onPlaying);
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('error', onError);
    };
  }, [viewUrl, viewed, duration, isAuthed]);

  // Prepare streaming source with HLS support
  useEffect(() => {
    const el = videoRef.current;
    if (!el || effectiveGated) return;
    let cancelled = false;
    (async () => {
      setErr(null);
      setLoading(true);
      // Ensure prior HLS instance fully torn down before re-init
      try { if (hlsRef.current && typeof hlsRef.current.destroy === 'function') { hlsRef.current.destroy(); hlsRef.current = null; } } catch {}
      let url = streamUrl;
      try {
        const res = await apiFetch(streamUrl, session, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const j: any = await res.json().catch(() => null);
          if (!res.ok) {
            const code = String(j?.error || '').toLowerCase();
            if (code === 'not_ready') {
              setErr('Video not ready for playback.');
              setLoading(false);
              return; // don't proceed setting src
            }
            if (res.status === 403) setErr('You do not have permission to play this video.');
            else if (res.status === 401) setErr('Please log in to play this video.');
            // 404 = not visible to this viewer (audience gate) or gone. Never
            // permission wording here: that would confirm the video exists.
            else if (res.status === 404) setErr("This video isn't available.");
            else setErr('Sorry, this video cannot be played. Please try again later.');
            setLoading(false);
            try { postAnalytics('video_stream_error', { id, status: res.status, code, ua: typeof navigator !== 'undefined' ? navigator.userAgent : '' }); } catch {}
            return;
          }
          const candidate = j?.url || j?.Url || j?.streamUrl || j?.StreamUrl || j?.href || j?.Href;
          if (candidate && typeof candidate === 'string') url = candidate;
        } else if (!res.ok) {
          if (res.status === 403) setErr('You do not have permission to play this video.');
          else if (res.status === 401) setErr('Please log in to play this video.');
          else if (res.status === 404) setErr("This video isn't available.");
          else setErr('Sorry, this video cannot be played. Please try again later.');
          setLoading(false);
          try { postAnalytics('video_stream_error', { id, status: res.status, ua: typeof navigator !== 'undefined' ? navigator.userAgent : '' }); } catch {}
          return;
        }
      } catch {
        // network failure: show generic error and stop
        setErr('Sorry, this video cannot be played. Please try again later.');
        setLoading(false);
        // Avoid spamming stream_error when the device is offline; offline event already captured
        try {
          const isOfflineNow = typeof navigator !== 'undefined' ? (navigator.onLine === false) : false;
          if (!isOfflineNow) postAnalytics('video_stream_error', { id, status: 'network', ua: typeof navigator !== 'undefined' ? navigator.userAgent : '' });
        } catch {}
        return;
      }

      if (cancelled) return;
      const isHls = /\.m3u8(\?|$)/i.test(url) || /format=hls/i.test(url);
      try { if (onStreamResolved && url) onStreamResolved(url); } catch {}
      try {
        // Analytics: log stream init and configured bitrate cap
        try {
          const transport = isHls ? (el.canPlayType('application/vnd.apple.mpegurl') ? 'hls-native' : 'hlsjs') : 'progressive';
          postAnalytics('video_stream_init', { id, transport, maxInitialBitrate: isHls ? MAX_INITIAL_BITRATE : undefined });
          if (debugMediaPerf) {
            // eslint-disable-next-line no-console
            console.log('[MEDIA] stream_init', { transport, maxInitialBitrate: isHls ? MAX_INITIAL_BITRATE : undefined });
          }
        } catch {}

        if (isHls) {
          // Safari has native HLS support
          if (el.canPlayType('application/vnd.apple.mpegurl')) {
            el.src = url;
          } else {
            // Lazy-load hls.js
            try {
              const mod: any = (await import('hls.js')).default || (await import('hls.js'));
              const Hls = mod?.default || mod;
              if (Hls && Hls.isSupported()) {
                const hls = new Hls({ enableWorker: true, maxInitialBitrate: MAX_INITIAL_BITRATE, capLevelToPlayerSize: true, startLevel: -1 });
                hlsRef.current = hls;
                hls.loadSource(url);
                hls.attachMedia(el);
                hls.on(Hls.Events.ERROR, () => {
                  if (!cancelled) setErr('Sorry, this video cannot be played. Please try again later.');
                });
              } else {
                // Fallback: set src, some browsers may still attempt
                el.src = url;
              }
            } catch {
              // If hls.js not available, fallback to direct
              el.src = url;
            }
          }
        } else {
          // Progressive MP4 (or other)
          el.src = url;
        }
      } catch {
        if (!cancelled) setErr('Sorry, this video cannot be played. Please try again later.');
      }
    })();
    return () => {
      cancelled = true;
      try { if (hlsRef.current && typeof hlsRef.current.destroy === 'function') { hlsRef.current.destroy(); hlsRef.current = null; } } catch {}
    };
  }, [streamUrl, effectiveGated, retrySeq]);

  return (
    <div className="space-y-3">
      <div className="relative w-full rounded-lg overflow-hidden bg-black aspect-video">
        <p id={`vid-help-${id}`} className="sr-only">
          Use space to play or pause. Use arrow keys to adjust volume. Use F for fullscreen.
        </p>
        {/* Phase D §D.5 — AirPlay: `x-webkit-airplay="allow"` makes the
            AirPlay picker appear in Safari's native controls automatically
            when an AirPlay receiver (Apple TV, AirPlay-compatible speaker)
            is on the local network. No JS needed. Non-Safari browsers
            ignore the attribute. */}
        <video
          ref={videoRef}
          controls={!effectiveGated}
          preload="auto"
          className={`w-full h-full ${effectiveGated ? 'blur-xl pointer-events-none' : ''}`}
          poster={poster || undefined}
          playsInline
          x-webkit-airplay="allow"
          aria-label={title || 'Video player'}
          aria-describedby={`vid-help-${id}`}
        />
        {!effectiveGated && loading && (
          // pointer-events-none is load-bearing (2026-07-24): this overlay is
          // transparent and covers the whole player INCLUDING the native
          // control bar, and `waiting` re-raises it on every HLS rebuffer —
          // without it, an invisible wall eats every click on the timeline
          // ("plays but can't seek", video 230, caught via elementFromPoint).
          <div ref={spinRef} className="absolute inset-0 grid place-items-center pointer-events-none" role="dialog" aria-modal="true" aria-live="polite" tabIndex={-1} onKeyDown={onTrapKeyDown}>
            <div className="h-10 w-10 rounded-full border-4 border-white/40 border-t-white animate-spin" aria-label="Loading video" />
            <div className="sr-only" aria-live="polite">Loading video…</div>
          </div>
        )}
        {!effectiveGated && offline && (
          <div ref={offRef} className="absolute inset-0 grid place-items-center bg-black/60" role="dialog" aria-modal="true" aria-live="polite" tabIndex={-1} onKeyDown={onTrapKeyDown}>
            <div className="text-center text-white text-sm px-4 space-y-3">
              <div id="offline-msg">You’re offline. We’ll resume when you reconnect.</div>
              <Button aria-describedby="offline-msg" size="sm" variant="secondary" onClick={() => {
                try {
                  if (hlsRef.current && typeof hlsRef.current.destroy === 'function') { hlsRef.current.destroy(); hlsRef.current = null; }
                  if (videoRef.current) { videoRef.current.removeAttribute('src'); videoRef.current.load?.(); }
                } catch {}
                setErr(null); setLoading(true);
                setRetrySeq(s => s + 1);
              }}>
                Retry now
              </Button>
            </div>
          </div>
        )}
        {!effectiveGated && !offline && err && (
          <div ref={errRef} className="absolute inset-0 grid place-items-center bg-black/60" role="dialog" aria-modal="true" aria-live="assertive" tabIndex={-1} onKeyDown={onTrapKeyDown}>
            <div className="text-center text-white text-sm px-4 space-y-3">
              <div id="err-msg">{err}</div>
              <Button aria-describedby="err-msg" size="sm" variant="secondary" onClick={() => {
                try {
                  if (hlsRef.current && typeof hlsRef.current.destroy === 'function') { hlsRef.current.destroy(); hlsRef.current = null; }
                  if (videoRef.current) { videoRef.current.removeAttribute('src'); videoRef.current.load?.(); }
                } catch {}
                setErr(null); setLoading(true);
                setRetrySeq(s => s + 1);
              }}>
                Retry
              </Button>
            </div>
          </div>
        )}
        {effectiveGated && (
          <div ref={gateRef} className="absolute inset-0 grid place-items-center bg-black/30" role="dialog" aria-modal="true" aria-live="assertive" tabIndex={-1} onKeyDown={onTrapKeyDown}>
            <div className="sr-only" aria-live="assertive">Login required to play this video.</div>
            <Button
              onClick={() => (window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname))}
              aria-label={unsafe ? 'Sensitive content - log in to view' : 'Log in to view'}
              size="sm"
            >
              {unsafe ? 'Sensitive content - Log in to view' : 'Log in to view'}
            </Button>
          </div>
        )}
      </div>
      {/* Phase D §D.5 — Player extras row. PiP button is feature-detected.
          AirPlay shows in Safari's native controls (attribute on <video>);
          no button needed here. Chromecast deferred until §D.3 signed
          cookies ship (the cast receiver fetches the URL directly and
          won't have the sender's cookies). */}
      {pipSupported && !effectiveGated && !err && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { void togglePiP(); }}
            aria-pressed={pipActive}
            aria-label={pipActive ? 'Exit picture-in-picture' : 'Enter picture-in-picture'}
            className="h-7 px-2"
          >
            {pipActive ? 'Exit PiP' : 'PiP'}
          </Button>
        </div>
      )}
      {showUnderPlayerMeta && (
      <div>
        {React.createElement(titleHeading, { className: 'font-semibold' }, scrubContactInfo(title))}
        {typeof views === 'number' && (
          <p className="text-xs text-muted-foreground">{views} view{views === 1 ? '' : 's'}</p>
        )}
        <div className="mt-1 text-xs text-muted-foreground flex flex-wrap items-center gap-2">
          {uploader?.displayName && (
            <a href={uploader.profileUrl || '#'} className="underline hover:no-underline">{scrubContactInfo(uploader.displayName)}</a>
          )}
          {duration > 0 && (
            <span>• {new Date(duration * 1000).toISOString().substring(duration >= 3600 ? 11 : 14, 19)}</span>
          )}
          {createdAt && (
            <span>• {new Date(createdAt).toLocaleDateString()}</span>
          )}
        </div>
        {/* Centralize likes in the top toolbar; remove under-player like UI */}
        {typeof commentCount === 'number' && (
          <div className="mt-2 text-xs text-muted-foreground">{commentCount} comment{commentCount === 1 ? '' : 's'}</div>
        )}
        {description && (
          <p className="mt-2 text-sm text-muted-foreground whitespace-pre-line">{scrubContactInfo(description)}</p>
        )}
        {err && (
          <p className="mt-2 text-xs text-yellow-700" aria-live="polite">{err}</p>
        )}
      </div>
      )}
    </div>
  );
}
